<template>
  <v-card flat class="white vetalfa-view" :max-width="_getMaxWidth()">
    <div ref="screenHeader" class="vetalfa-global-header">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        :breadcrumb="_getLabel('CLINICAL_HANDOUTS')"
        :title="_getLabel('CLINICAL_HANDOUTS')"
        hasSlotOption
      >
        <template v-slot:headerSearch>
          <v-row
            justify="center"
            :class="`primary pa-0 pt-${
              _isCordovaApp() || $vuetify.breakpoint.xsOnly ? 0 : 4
            } py-4 ma-0`"
          >
            <v-row class="vetalfa-max-width pa-0 ma-0 px-4">
              <v-col class="ma-0 pa-0" :cols="12">
                <v-text-field
                  dense
                  outlined
                  clearable
                  :value="search"
                  :disabled="loading"
                  :loading="loading"
                  v-model="search"
                  class="white"
                  :placeholder="_getLabel('SEARCH_TEXT')"
                  ref="searchField"
                  :style="{ borderRadius: '10px' }"
                  hide-details
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary" class="mr-0">
                      {{ _icon("magnify") }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-row>
        </template>
        <template>
          <v-icon
            @click="showHowItWorks"
            size="18"
            :color="_isMobile() ? 'white' : 'primary'"
            class="mx-1 pt-1"
            >{{ mdiHelpCircle }}</v-icon
          >
        </template>
      </vetalfa-small-header>

      <v-row justify="center" class="pa-0 ma-0">
        <v-row no-gutters class="vetalfa-max-width">
          <v-chip-group
            v-model="chipFilterContentType"
            dense
            class="ma-0 pa-0 white ml-3 py-2"
            active-class="primary white--text"
            v-if="!loading"
          >
            <v-icon
              @click="chipFilterContentType = undefined"
              v-if="chipFilterContentType !== undefined"
              color="primary"
              class="ma-0 pa-0 mr-2 vetalfa-pointer"
            >
              {{ mdiCloseCircleOutline }}
            </v-icon>
            <v-chip
              v-for="(type, typeIndex) in contentTypeOrder"
              :key="`chip-group-${type}`"
              v-show="
                chipFilterContentType === undefined ||
                chipFilterContentType == typeIndex
              "
              small
              :outlined="chipFilterContentType != typeIndex"
              color="primary"
              >{{ getContentTypeName(type) }}</v-chip
            >
          </v-chip-group>
        </v-row>
      </v-row>
    </div>
    <div ref="screenContent">
      <v-skeleton-loader
        v-if="loading && virtualScrollHeightComputed > 0"
        class="ma-0 pa-0"
        :type="skeletonType"
      ></v-skeleton-loader>
      <v-virtual-scroll
        v-else-if="!loading && virtualScrollHeightComputed > 0"
        :items="availableItems"
        :item-height="70"
        :bench="0"
        :height="virtualScrollHeightComputed"
        id="items-virtual-scroll"
        class="ma-0 pa-0"
      >
        <template v-slot="{ item }">
          <v-row v-if="item.id" class="ma-0 pa-0 px-2">
            <v-checkbox
              v-if="item.name"
              v-model="selected[item.id]"
              :value="undefined"
            ></v-checkbox>
            <v-card
              v-if="item.name"
              outlined
              height="60px"
              style="
                width: 90%;
                border: 1px solid #eeeeee !important;
                border-radius: 10px;
              "
              class="ma-0 pa-0 white px-2 vetalfa-pointer"
              @click="cardClick(item)"
            >
              <v-row class="ma-0 pa-0 vetalfa-row" align="center">
                <v-col :cols="11" class="ma-0 pa-0">
                  <v-row class="ma-0 pa-0" align="center">
                    <p
                      v-html="item.name"
                      class="ma-0 items-title items-two-lines-clamp"
                    ></p>
                  </v-row>
                  <v-row class="ma-0 pa-0">
                    <p
                      class="items-description-label text-center mt-1 ma-0 pa-0"
                    >
                      {{ getContentTypeName(item.content_type) }}
                    </p>
                  </v-row>
                </v-col>
                <v-col :cols="1" class="ma-0 pa-0">
                  <v-row justify="end" class="ma-0 pa-0 pr-1">
                    <v-icon x-small color="#747678">{{
                      mdiChevronRight
                    }}</v-icon>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-row>
        </template>
      </v-virtual-scroll>

      <vetalfa-dialog
        v-on:dialogClose="selectionModel = false"
        :bottom="false"
        :toggleModel="selectionModel"
        :fullscreen="_xsOnly()"
        max-width="700px"
        :smallHeader="false"
        :featureMode="false"
        :height="dialogHeight"
      >
        <template v-slot:header>
          <v-row
            no-gutters
            align="center"
            justify="space-between"
            class="px-2 vetalfa-padding-top-notch mt-4"
          >
            <v-col cols="10">
              <v-row no-gutters align="start" class="vetalfa-row">
                <v-icon size="22" color="primary" class="mx-1 pt-1">{{
                  _icon("handouts")
                }}</v-icon>
                <span
                  class="ma-0 pa-0 ml-1 primary--text subtitle-1 font-weight-bold"
                >
                  {{ _getLabel("CLINICAL_HANDOUTS") }}
                </span>
              </v-row>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="1">
              <v-row no-gutters class="vetalfa-row" align="start">
                <v-btn :disabled="loading" icon @click="selectionModel = false">
                  <v-icon size="20" color="primary">{{ mdiClose }}</v-icon>
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </template>
        <div v-if="!confirmEmailOrPhone">
          <v-col cols="12" class="px-4 my-2">
            <patients />
          </v-col>

          <v-col cols="12" no-gutters class="ma-0 pa-0">
            <v-row no-gutters class="mt-6 mx-5">
              <v-list dense class="white" style="width: 100%">
                <p class="primary--text">
                  Como você deseja compartilhar com o tutor?
                </p>
                <v-list-item-group v-model="actionsModel" active-class="">
                  <template v-for="(action, index) in shareActions">
                    <v-list-item :key="action.title">
                      <template v-slot:default="{ active }">
                        <v-list-item-action>
                          <v-checkbox :input-value="active"></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            action.title
                          }}</v-list-item-title>
                          <span
                            v-if="action.subtitle && active"
                            class="caption grey--text text--darken-1"
                          >
                            {{ action.subtitle }}
                          </span>
                        </v-list-item-content>
                        <v-list-item-icon>
                          <v-icon size="20" color="primary" class="mt-1">{{
                            action.icon
                          }}</v-icon>
                        </v-list-item-icon>
                      </template>
                    </v-list-item>

                    <v-divider
                      v-if="index < shareActions.length - 1"
                      :key="index"
                    ></v-divider>
                  </template>
                </v-list-item-group>
              </v-list>
            </v-row>
          </v-col>
          <v-col cols="12" class="ma-0 mt-5 px-4 py-0">
            <p class="primary--text subtitle-2 pa-0 ma-0 mb-2">
              {{ _getLabel("CLINICAL_HANDOUTS_SELECTED") }}
            </p>
          </v-col>
          <v-col
            v-if="availableItems.length && selectionModel"
            cols="12"
            no-gutters
            class="ma-0 pl-4 pr-3 py-0"
          >
            <v-row
              no-gutters
              v-for="item in selectionObjects"
              :key="item.name"
              justify="space-between"
              align="center"
              class="mb-3"
            >
              <v-col cols="11">
                <v-card
                  v-if="item.name"
                  outlined
                  height="60px"
                  style="
                    border: 1px solid #eeeeee !important;
                    border-radius: 10px;
                  "
                  class="ma-0 pa-0 white px-2 mr-3 vetalfa-pointer"
                  @click="cardClick(item)"
                >
                  <v-row class="ma-0 pa-0 vetalfa-row" align="center">
                    <v-col :cols="11" class="ma-0 pa-0">
                      <v-row class="ma-0 pa-0" align="center">
                        <p
                          v-html="item.name"
                          class="ma-0 items-title items-two-lines-clamp"
                        ></p>
                      </v-row>
                      <v-row class="ma-0 pa-0">
                        <p
                          class="items-description-label text-center mt-1 ma-0 pa-0"
                        >
                          {{ getContentTypeName(item.content_type) }}
                        </p>
                      </v-row>
                    </v-col>
                    <v-col :cols="1" class="ma-0 pa-0">
                      <v-row justify="end" class="ma-0 pa-0 pr-1">
                        <v-icon x-small color="#747678">{{
                          mdiChevronRight
                        }}</v-icon>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
              <v-col cols="1">
                <v-icon
                  @click="removeItemFromSelection(item)"
                  color="primary"
                  class="mr-4"
                  small
                  >{{ mdiCloseCircleOutline }}</v-icon
                ></v-col
              >
            </v-row>
          </v-col>
        </div>
        <algorithms
          class="mt-6"
          :headerHeightOffset="50"
          v-if="confirmEmailOrPhone"
        >
        </algorithms>

        <template v-slot:actions>
          <v-col v-if="!confirmEmailOrPhone" cols="12" class="mb-4">
            <v-row class="ma-0 mb-4" justify="center">
              <v-btn
                text
                large
                v-if="!loading"
                @click="selectionModel = false"
                class="subtitle-2 text-decoration-underline primary--text text-none px-12"
              >
                {{ _getLabel("CLINICAL_HANDOUTS_SELECT_MORE") }}
              </v-btn>
            </v-row>
            <v-row class="ma-0 vetalfa-padding-bottom-notch" justify="center">
              <v-btn
                rounded
                :loading="loading"
                large
                :disabled="actionsModel === undefined"
                class="subtitle-1 primary white--text text-none px-12"
                @click="shareClinicalHandoutsClick(false)"
              >
                {{ _getLabel("CONTINUE") }}
              </v-btn>
            </v-row>
          </v-col>
        </template>
      </vetalfa-dialog>
      <v-btn
        large
        color="primary"
        :disabled="selectionSize == 0"
        @click="startClinicalHandoutShare"
        fixed
        rounded
        bottom
        class="text-none px-16"
        style="left: 50%; transform: translateX(-50%); bottom: 30px"
        >{{ _getLabel("CLINICAL_HANDOUTS_SHARE") }}

        <v-badge
          overlap
          icon
          bordered
          color="primary"
          class="ma-0 pa-0 white--text"
          :content="selectionSize"
          :value="selectionSize"
          style="position: relative; bottom: 24px; left: 40%"
        >
        </v-badge>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Patients from "@/components/Patients.vue";
import Algorithms from "@/algorithms/Algorithms.vue";
import {
  mdiChevronRight,
  mdiCloseCircleOutline,
  mdiHelpCircle,
  mdiEmailOutline,
  mdiClose,
  mdiLink,
} from "@mdi/js";
const SHARE_ACTIONS = {
  PRINT: 1,
  EMAIL: 2,
  WHATSAPP: 3,
  SAVE: 4,
  COPY: 5,
};
export default {
  name: "ClinicalHandouts",
  components: {
    patients: Patients,
    algorithms: Algorithms,
  },
  methods: {
    ...mapActions("topics", ["openTopic", "shareClinicalHandouts"]),
    ...mapActions("general", [
      "logEvent",
      "displayHelpArticle",
      "routePathRedirect",
      "showAlert",
    ]),
    ...mapMutations("topics", ["setClinicalHandouts"]),
    ...mapMutations("general", ["setConfirmModel"]),
    ...mapActions("patients", [
      "confirmOwnerEmailAlgorithm",
      "confirmOwnerPhoneAlgorithm",
      "confirmOwnerPhoneAndEmailAlgorithm",
    ]),
    async startClinicalHandoutShare() {
      const hasCompleteRegistration =
        this.isVeterinaryProfessional &&
        this.user.crmvNumber &&
        this.user.crmvState;

      if (!hasCompleteRegistration) {
        return this.setConfirmModel({
          text: this._getLabel("HANDOUTS_MEDICAL_REGISTRATION_DETAILS"),
          callback: () => {
            this.routePathRedirect({ path: "/registration/crmv" });
          },
          cancelAction: () => {
            this.selectionModel = false;
          },
          confirmText: this._getLabel("HAS_CRMV"),
          closeText: this._getLabel("CANCEL"),
          persistent: true,
        });
      }
      this.selectionModel = true;
    },
    async shareClinicalHandoutsClick(hasRegistrationConfirmation = false) {
      if (!this.patient) {
        return this.showAlert({
          text: this._getLabel("CLICK_TO_SELECT_PATIENT"),
        });
      }
      const type = this.shareActions[this.actionsModel].type;
      const hasEmail = type === SHARE_ACTIONS.EMAIL;
      const hasWhatsapp = type === SHARE_ACTIONS.WHATSAPP;
      if (hasEmail && hasWhatsapp && !hasRegistrationConfirmation) {
        this.confirmEmailOrPhone = true;
        return this.confirmOwnerPhoneAndEmailAlgorithm();
      } else if (hasEmail && !hasRegistrationConfirmation) {
        this.confirmEmailOrPhone = true;
        return this.confirmOwnerEmailAlgorithm();
      } else if (hasWhatsapp && !hasRegistrationConfirmation) {
        this.confirmEmailOrPhone = true;
        return this.confirmOwnerPhoneAlgorithm();
      }
      this.confirmEmailOrPhone = false;
      const successResponse = await this.shareClinicalHandouts({
        type,
        email: this.getModels.email || undefined,
        phone: this.getModels.phone || undefined,
        ownerId: this.patient.ownerId,
        patientId: this.patient.id,
      });
      this.selectionModel = false;
      if (successResponse) {
        this.selected = {};
      }
    },
    cardClick(item) {
      this.logEvent({ name: "handouts_card_click", object: { id: item.id } });
      this.routePathRedirect({ path: `/handouts/${item.id}?fromMenu=true` });
    },
    showHowItWorks() {
      this.logEvent("handouts_how_it_works");
      this.displayHelpArticle({ id: process.env.VUE_APP_FAQ_HANDOUTS });
    },
    getContentTypeName(content_type) {
      return this._getLabel(
        {
          1: "SYSTEMIC_DRUGS",
          2: "DERMATOLOGICAL_TOPICS",
          3: "OPTHALMIC_AGENTS",
          4: "DISEASES_AND_TREATMENT",
        }[content_type]
      );
    },
    removeItemFromSelection(item) {
      let updatedSelection = { ...this.selected };
      delete updatedSelection[item.id];
      this.setClinicalHandouts(updatedSelection);
    },
    getItemFromId(id) {
      return this.items.find((item) => item.id == id);
    },
  },
  computed: {
    ...mapGetters("topics", ["items", "clinicalHandouts"]),
    ...mapGetters("general", ["isVeterinaryProfessional", "user"]),
    ...mapGetters("patients", ["patient"]),
    ...mapGetters("algorithms", ["flowEndTrigger", "getModels"]),

    shareActions() {
      return [
        {
          icon: mdiEmailOutline,
          title: "Compartilhar por E-mail",
          type: SHARE_ACTIONS.EMAIL,
          subtitle:
            "Enviaremos o link de acesso para o e-mail do tutor selecionado.",
        },
        {
          icon: this._icon("whatsapp"),
          title: this._getLabel("WHATSAPP"),
          type: SHARE_ACTIONS.WHATSAPP,
          subtitle:
            "Se você não for redirecionado, verifique o bloqueio de pop-ups ou copie o link abaixo e envie para o tutor.",
        },
        {
          icon: mdiLink,
          title:
            navigator.share && this._isMobile()
              ? "Compartilhar Link"
              : "Copiar link para envio manual",
          type: SHARE_ACTIONS.COPY,
          subtitle: this._isCordovaApp()
            ? "Você pode copiar o link de acesso e compartilhar com o Whatsapp e outros aplicativos do seu dispositivo."
            : null,
        },
      ];
    },
    virtualScrollHeightComputed() {
      return this.scrollHeight;
    },
    selectionObjects() {
      let arr = [];
      if (this.items && this.items.length && this.clinicalHandouts) {
        for (let key in this.clinicalHandouts) {
          if (this.clinicalHandouts[key]) {
            arr.push(this.getItemFromId(key));
          }
        }
      }
      return arr;
    },
    availableItems() {
      const available = this.items.filter(
        (item) =>
          (this.chipFilterContentType === undefined ||
            item.content_type ==
              this.contentTypeOrder[this.chipFilterContentType]) &&
          (!this.search ||
            this._removeAccents(item.name.toLowerCase()).includes(
              this._removeAccents(this.search.toLowerCase())
            ))
      );
      return [...available, {}, {}, {}];
    },
    selected: {
      get() {
        return this.clinicalHandouts;
      },
      set(value) {
        for (let key in value) {
          if (value[key] === false) {
            delete value[key];
          }
        }
        this.setClinicalHandouts(value);
      },
    },
    selectionSize() {
      return Object.values(this.clinicalHandouts).filter((v) => v).length;
    },
  },
  data() {
    return {
      search: "",
      actionsModel: undefined,
      contentTypeOrder: [4, 1, 2, 3],
      scrollHeight: 0,
      loading: false,
      mdiChevronRight,
      mdiCloseCircleOutline,
      mdiEmailOutline,
      mdiLink,
      mdiHelpCircle,
      chipFilterContentType: undefined,
      selectionModel: false,
      mdiClose,
      confirmEmailOrPhone: false,
      skeletonType:
        "chip,heading,heading,chip,heading,heading,heading,heading,chip,heading,heading,heading,heading,heading,chip,heading,heading,heading,heading",
      dialogHeight: null,
    };
  },
  watch: {
    selected() {
      this._setupScreenSizes(this);
      if (!Object.values(this.clinicalHandouts).filter((v) => v).length)
        this.selectionModel = false;
    },
    "$route.query": {
      handler(val) {
        if (val.faq) {
          this.$router.replace({ query: {} });
          this.showHowItWorks();
        }
      },
      deep: true,
      immediate: true,
    },
    selectionModel(val) {
      if (val) {
        this._setupScreenSizes(this);
        this.confirmEmailOrPhone = false;
      }
    },
    flowEndTrigger() {
      if (this.confirmEmailOrPhone) {
        this.shareClinicalHandoutsClick(true);
      }
    },
  },
  async mounted() {
    this._setupScreenSizes(this);
    try {
      this.loading = true;
      await this.openTopic("clinical_handouts");
      this._setupScreenSizes(this);
    } finally {
      this.loading = false;
      this.dialogHeight = this._getDialogHeight(0.6);
    }
  },
  async created() {},
};
</script>
